/**
 * The site's Schema.org graph.
 *
 * @remarks
 * Every entity the site declares, in one file. Two rules govern it, and both are the reason
 * `@bayudwiyansatria/seo` exists:
 *
 * 1. **One `@id` per real-world entity, declared once.** Reference with `seo.ref()` everywhere else.
 *    A repeated declaration describes that many separate things, and nothing reports it — the JSON
 *    stays valid, both validators pass, and the rich result simply never appears.
 * 2. **The site-wide entities belong to the home route only.** `primaryEntity` and `website` anchor
 *    the graph; the route table emits them on `/` and nowhere else.
 *
 * `test/config/entities.spec.ts` asserts the first rule with `assertUniqueEntityIds`.
 *
 * **This file holds no facts.** Every value comes from `identity.ts` or `site.ts`; what lives here
 * is the mapping from those facts onto Schema.org — which property a fact belongs in, and which of
 * the two entity shapes it applies to. A fork edits the configuration, not this file.
 *
 * **Relative imports only** — reachable from `vite.config.ts`.
 *
 * @module
 * @author Bayu Dwiyan Satria
 * @since 1.0.0
 */

/**
 * Libraries
 */
import { omitEmpty, SCHEMA_CONTEXT } from '@bayudwiyansatria/seo'

/**
 * Config
 */
import { documentationConfig } from './documentation'
import { faqItems } from './faq'
import { identityConfig } from './identity'
import { seo } from './seo'
import { siteConfig } from './site'
import { softwareConfig } from './software'
import { testimonials } from './testimonials'

/**
 * Types
 */
import type {
  ContactPointInput,
  JsonLd,
  LanguageInput,
  OrganizationRefInput,
  PlaceInput,
  PostalAddressInput
} from '@bayudwiyansatria/seo'

/**
 * An empty string means "not configured", which in JSON-LD means the property must be absent.
 *
 * @remarks
 * A property present but blank is worse than a missing one: it asserts that the entity has an empty
 * address, and a consumer has no way to read that as "unknown".
 */
function text(value: string): string | undefined {
  return value.trim() || undefined
}

/**
 * An organisation the primary entity is tied to, as a reference where possible.
 *
 * @remarks
 * A configured `id` becomes a bare `@id` reference, which is what lets the node merge with the
 * entity that organisation's own site publishes. Without one there is nothing to merge with, so the
 * details are inlined instead — a weaker claim, and the reason the field documentation asks for an
 * `id` on their domain.
 */
function organizationNode(organization: OrganizationRefInput): JsonLd {
  return organization.id
    ? seo.ref(organization.id)
    : omitEmpty({
        '@type': organization.type ?? 'Organization',
        name: organization.name,
        alternateName: organization.alternateName,
        url: organization.url
      })
}

/**
 * A language node, mirroring what the package emits for a `Person`.
 *
 * @remarks
 * Needed because `OrganizationInput` does not model `knowsLanguage` — Schema.org allows it on both,
 * so the Organization branch passes it through `extra` and has to build the node itself. Keep this
 * identical to the package's mapping; two shapes for one property is exactly the drift the graph is
 * supposed to prevent.
 */
function languageNode(language: LanguageInput): JsonLd {
  return typeof language === 'string'
    ? { '@type': 'Language', name: language }
    : omitEmpty({ '@type': 'Language', name: language.name, alternateName: language.code })
}

/** Whether any part of the configured location is set. */
const hasLocation = Boolean(text(identityConfig.location.city) || text(identityConfig.location.country))

/** The configured address, or `undefined` when no part of it is set. */
const postalAddress: PostalAddressInput | undefined = hasLocation
  ? {
      locality: text(identityConfig.location.city),
      region: text(identityConfig.location.region),
      postalCode: text(identityConfig.location.postalCode),
      country: text(identityConfig.location.country)
    }
  : undefined

/**
 * The place the entity is based, named for humans and addressed for machines.
 *
 * @remarks
 * The name is the city alone. `country` holds an ISO code because that is what `addressCountry`
 * expects, and a code reads as noise in a display name.
 */
const primaryLocation: PlaceInput | undefined = text(identityConfig.location.city)
  ? { name: identityConfig.location.city, address: postalAddress }
  : undefined

/**
 * How to reach the entity.
 *
 * @remarks
 * Omitted entirely when neither an email nor a telephone is published. A `contactPoint` that
 * carries no way to make contact is a node describing nothing.
 */
const contactPoint: ContactPointInput | undefined =
  text(identityConfig.email) || text(identityConfig.telephone)
    ? {
        contactType: identityConfig.type === 'Person' ? 'professional' : 'customer support',
        email: text(identityConfig.email),
        telephone: text(identityConfig.telephone),
        availableLanguage: identityConfig.languages.map(language =>
          typeof language === 'string' ? language : language.name
        )
      }
    : undefined

/** Every title a Person holds, primary role first. Not used for an Organization, which has a slogan. */
const jobTitles = [identityConfig.role, ...identityConfig.jobTitles].filter(Boolean)

/**
 * The `@id` every reference to the primary entity uses.
 *
 * @remarks
 * `identityConfig.canonicalId` when another site already publishes this entity, and this site's own
 * anchor otherwise. Resolved once, here, because the cost of getting it wrong is silent: a page whose
 * `about` names an `@id` that nothing declares is a dangling reference, and JSON-LD has no notion of
 * a broken link to report. See {@link IdentityConfig.canonicalId} for why an external id is
 * preferable to a local copy.
 */
const entityId = text(identityConfig.canonicalId) ?? seo.ids.entity

/** That id as a reference, for the many places it is pointed at rather than declared. */
const entityRef = seo.ref(entityId)

/**
 * The entity references `seo.page()` fills in from this site's own anchor.
 *
 * @remarks
 * The package resolves `about` and `mainEntity` against `seo.ids.entity`, which is correct only while
 * the entity is declared on this origin. Spread into each page's `extra` — which the builder applies
 * last — so `about` follows `entityId` instead.
 *
 * **`mainEntity` is removed rather than repointed, and that is the important half.** A reference is
 * only resolvable to a consumer that can see the node it names, and the entity is declared on the
 * home route — on another origin, at that. Every other page was therefore claiming a principal
 * subject whose type nothing in the document states, which validators reject outright with
 * *Invalid object type for field mainEntity*. It is also the wrong claim on its own terms: a contact
 * form's principal subject is not a person, and `about` already says the page concerns them.
 *
 * The one page whose subject genuinely *is* the entity states it by nesting the node — see
 * {@link authorPage}. `undefined` rather than omission because the builder sets the property itself
 * and `extra` is the only thing applied after it; `JSON.stringify` then drops the key.
 */
const pageEntityLinks: JsonLd = { about: entityRef, mainEntity: undefined }

/** The share image, reused as the entity's `image` so both describe the same file at the same size. */
const entityImage = {
  url: identityConfig.image,
  width: siteConfig.ogImage.width,
  height: siteConfig.ogImage.height,
  caption: identityConfig.name
}

/**
 * The `Person` or `Organization` the whole site is about.
 *
 * @remarks
 * Switched by `identityConfig.type`, so a fork changes one field rather than rewriting this file.
 * Google treats the two very differently in knowledge panels, so pick the one that is literally
 * true.
 *
 * The two branches are not the same entity with a different `@type`. A `Person` carries the
 * credentials, education and publications that make an individual identifiable; an `Organization`
 * carries the legal name and support contact that make a company identifiable, and takes its ties
 * to other organisations through `parentOrganization` and `memberOf` rather than `worksFor` and
 * `affiliation`. Properties absent from one branch are absent because Schema.org does not define
 * them there, not because they were forgotten.
 */
export const primaryEntity: JsonLd =
  identityConfig.type === 'Person'
    ? seo.person({
        name: identityConfig.name,
        givenName: text(identityConfig.givenName),
        familyName: text(identityConfig.familyName),
        alternateName: identityConfig.alternateName,
        description: identityConfig.description,
        jobTitle: jobTitles,
        url: text(identityConfig.url),
        email: text(identityConfig.email),
        telephone: text(identityConfig.telephone),
        address: postalAddress,
        image: entityImage,
        worksFor: identityConfig.worksFor,
        alumniOf: identityConfig.alumniOf,
        knowsAbout: identityConfig.topics,
        hasOccupation: identityConfig.occupations,
        hasCredential: identityConfig.credentials,
        affiliation: identityConfig.affiliations,
        identifier: identityConfig.identifiers,
        nationality: text(identityConfig.nationality),
        knowsLanguage: identityConfig.languages,
        homeLocation: primaryLocation,
        workLocation: text(identityConfig.workLocation),
        contactPoint,
        publications: identityConfig.publications,
        /*
         * `extra` is applied after the builder's own properties, which is the only way to replace the
         * `@id` it derives from this site's anchor. The Organization branch below takes the id as an
         * argument instead — the package models the two differently.
         */
        extra: { '@id': entityId }
      })
    : seo.organization(
        {
          name: identityConfig.name,
          legalName: identityConfig.name,
          alternateName: identityConfig.alternateName,
          description: identityConfig.description,
          slogan: identityConfig.role,
          email: text(identityConfig.email),
          telephone: text(identityConfig.telephone),
          address: postalAddress,
          image: entityImage,
          knowsAbout: identityConfig.topics,
          identifier: identityConfig.identifiers,
          contactPoint,
          /*
           * `extra` is the package's escape hatch for properties it does not model. These two are the
           * Organization equivalents of the Person's worksFor and affiliation, and they take the same
           * anchoring rule: an `@id` on the other organisation's domain can merge with the entity
           * their own site publishes, where one on ours would create a second copy of them.
           */
          extra: omitEmpty({
            url: text(identityConfig.url),
            knowsLanguage: identityConfig.languages.map(languageNode),
            parentOrganization: identityConfig.worksFor ? organizationNode(identityConfig.worksFor) : undefined,
            memberOf: identityConfig.affiliations.map(organizationNode)
          })
        },
        entityId
      )

/**
 * The same entity as a nested node, for the properties that require the thing and not a pointer to it.
 *
 * @remarks
 * **This is not a second declaration.** It carries the identical `@id`, and an `@id` is what a
 * consumer merges on — the node here and the node on the home route are one entity described twice,
 * which is exactly what the `@id` discipline is for. What would be a second declaration is a copy
 * minted under a different id, and that cannot happen while this is derived from `primaryEntity`.
 *
 * It exists because a bare `{ "@id": … }` states no type, and the properties Google validates for a
 * rich result reject that: a `ProfilePage` naming a subject it does not describe fails with
 * *Invalid object type for field mainEntity*, and shows nothing of the person it was supposed to
 * profile. A reference is only usable where the node it names is in the same document.
 *
 * `@context` is stripped because this is nested inside a document that already declares one.
 */
const entityNode: JsonLd = Object.fromEntries(
  Object.entries(primaryEntity).filter(([property]) => property !== '@context')
)

/**
 * The site itself. Every page entity is `isPartOf` this.
 *
 * @remarks
 * The sitelinks search box is declared only when `siteConfig.search.urlTemplate` is set. Declaring
 * it against a page that ignores the query buys nothing and invites a consumer to send visitors to
 * a URL that does not search.
 *
 * The four entity references are repointed for the same reason the page entities are: the package
 * fills them from this site's own anchor, and this site's entity is declared under
 * `entityId`. `copyrightHolder` is the one that would be most quietly wrong — the footer
 * credits a person by name, and a graph crediting a different id would contradict it.
 */
export const website: JsonLd = seo.website({
  ...(siteConfig.search.urlTemplate ? { searchUrlTemplate: siteConfig.search.urlTemplate } : {}),
  extra: {
    publisher: entityRef,
    author: entityRef,
    about: entityRef,
    copyrightHolder: entityRef
  }
})

/** The rating scale every `Rating` and `AggregateRating` node on this site is expressed on. */
const RATING_SCALE = { bestRating: 5, worstRating: 1 }

/**
 * The reviews, as Schema.org nodes.
 *
 * @remarks
 * One node per entry in `testimonials.ts`, which is the same array `HomeView.vue` renders. Empty
 * when there are no testimonials, which is what keeps the software entity from claiming reviews
 * that are not on the page.
 */
const reviewNodes: JsonLd[] = testimonials.map(testimonial =>
  omitEmpty({
    '@type': 'Review',
    reviewRating: { '@type': 'Rating', ratingValue: testimonial.rating, ...RATING_SCALE },
    author: omitEmpty({ '@type': 'Person', name: testimonial.author, jobTitle: testimonial.role }),
    reviewBody: testimonial.quote,
    datePublished: testimonial.date,
    url: testimonial.url
  })
)

/**
 * The aggregate of those reviews, or `undefined` when there are none.
 *
 * @remarks
 * **Derived, never written.** `ratingValue` is the mean of the ratings the page displays and
 * `reviewCount` is how many it displays, so the aggregate cannot drift from the reviews and cannot
 * be edited into something the site did not earn. A hand-written figure here would be a rating for
 * content nobody rated — the case Google issues manual actions for.
 */
const aggregateRating: JsonLd | undefined =
  testimonials.length > 0
    ? {
        '@type': 'AggregateRating',
        ratingValue:
          Math.round((testimonials.reduce((total, item) => total + item.rating, 0) / testimonials.length) * 10) / 10,
        reviewCount: testimonials.length,
        ...RATING_SCALE
      }
    : undefined

/**
 * The software the site is about.
 *
 * @remarks
 * `undefined` when `softwareConfig.enabled` is `false` — a site that is not about a product should
 * not declare one.
 *
 * This is the entity Google reads for the Software App rich result, and it needs three things to be
 * eligible: a `name`, an `offers` carrying a `price`, and either an `aggregateRating` or a `review`.
 * The first two are configuration. The third only exists once `testimonials.ts` does, which is
 * deliberate — see the remarks in that file.
 */
export const softwareApplication: JsonLd | undefined = softwareConfig.enabled
  ? omitEmpty({
      '@context': SCHEMA_CONTEXT,
      '@type': softwareConfig.type,
      '@id': seo.ids.of('software'),
      name: softwareConfig.name,
      description: softwareConfig.description,
      url: seo.url('/'),
      applicationCategory: softwareConfig.applicationCategory,
      operatingSystem: softwareConfig.operatingSystem,
      softwareVersion: softwareConfig.version,
      programmingLanguage: [...softwareConfig.programmingLanguage],
      license: softwareConfig.license,
      codeRepository: softwareConfig.repository,
      featureList: [...softwareConfig.featureList],
      screenshot: softwareConfig.screenshot ? seo.url(softwareConfig.screenshot) : undefined,
      offers: {
        '@type': 'Offer',
        price: softwareConfig.offers.price,
        priceCurrency: softwareConfig.offers.priceCurrency,
        availability: 'https://schema.org/InStock',
        url: seo.url('/')
      },
      aggregateRating,
      review: reviewNodes,
      /*
       * Both point at the canonical entity id, which may be on another origin. That is the whole
       * value of the reference: the author of this software and the person that site publishes are
       * one entity, and a locally minted id would make them two.
       */
      author: entityRef,
      publisher: entityRef,
      isPartOf: seo.ref(seo.ids.website)
    })
  : undefined

/**
 * Its source repository.
 *
 * @remarks
 * A second entity rather than a property of the first, because they are two things: the application
 * is what a visitor uses and the source is what a contributor clones. `targetProduct` is the link
 * between them, which is why neither has to repeat the other's facts.
 */
export const sourceCode: JsonLd | undefined = softwareConfig.enabled
  ? omitEmpty({
      '@context': SCHEMA_CONTEXT,
      '@type': 'SoftwareSourceCode',
      '@id': seo.ids.of('sourcecode'),
      name: softwareConfig.name,
      description: softwareConfig.description,
      codeRepository: softwareConfig.repository,
      programmingLanguage: [...softwareConfig.programmingLanguage],
      runtimePlatform: softwareConfig.runtimePlatform,
      license: softwareConfig.license,
      version: softwareConfig.version,
      targetProduct: seo.ref(seo.ids.of('software')),
      author: entityRef
    })
  : undefined

/** Both software entities, in the order the home route emits them. Empty when the site has none. */
export const softwareEntities: readonly JsonLd[] = [softwareApplication, sourceCode].filter(
  (entity): entity is JsonLd => entity !== undefined
)

/**
 * The home route's page entity.
 *
 * @remarks
 * `mainEntity` is repointed at the software when there is any: the page is *about* the entity in the
 * sense that it publishes it, but what the page is primarily describing is the product. `about` is
 * left pointing at the entity, so both relationships stay stated.
 *
 * That is also what decides the page type, and the condition is narrower than it looks. A personal
 * site's home page is a `ProfilePage`, which is what Google reads when deciding whether a site
 * represents an individual — but a `ProfilePage` is a profile *of* the person it names, and its
 * `mainEntity` is required to be that person. So the two rules above collide the moment a site is
 * published by a `Person` *and* is about a product: the page would claim to profile someone while
 * declaring a piece of software as its subject.
 *
 * When there is a product, the product wins and the page is a plain `WebPage` — which is the honest
 * reading anyway, since a landing page for software is not a profile of its author. The `Person`
 * entity is unaffected and still carries the identity signals; it is simply the page's `about` and
 * the software's `author`, not its subject.
 */
export const homePage: JsonLd = seo.page({
  path: '/',
  name: siteConfig.title,
  description: siteConfig.description,
  type: identityConfig.type === 'Person' && !softwareApplication ? 'ProfilePage' : 'WebPage',
  extra: {
    ...pageEntityLinks,
    /*
     * The software is referenced rather than nested because this page declares it: the node is in
     * the same document, so a consumer resolves the reference without leaving it. Where there is no
     * product the page becomes the `ProfilePage` above, and that type requires its subject inline —
     * the same reason `authorPage` nests the node rather than pointing at it.
     */
    mainEntity: softwareApplication ? seo.ref(seo.ids.of('software')) : entityNode
  }
})

/** The about route. `AboutPage` marks the canonical place a site describes its own entity. */
export const aboutPage: JsonLd = seo.page({
  path: '/about',
  name: 'About',
  description:
    'What this boilerplate is, which patterns it standardises, and how the prerendering and structured-data layers fit together.',
  type: 'AboutPage',
  breadcrumb: [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' }
  ],
  extra: pageEntityLinks
})

/**
 * The author route.
 *
 * @remarks
 * The one page whose subject is the entity rather than the product, and the reason it exists is the
 * collision described on {@link homePage}: a `ProfilePage`'s `mainEntity` is required to be the
 * person it profiles, so a home route that declares the software as its subject cannot also be the
 * profile. Declaring it here leaves both statements true — the home page is about the product, and
 * the person has a page that is about them.
 *
 * **`mainEntity` is the node itself, not a reference to it** — see {@link entityNode}. This is the
 * one property on the site where the difference is not stylistic: a `ProfilePage` is required to
 * describe the person it profiles, and a pointer to a node declared on another page describes
 * nobody. A consumer reading this page in isolation, which is what every validator and every
 * crawler does, either finds the person here or finds an untyped `@id` and rejects the page.
 *
 * `about` stays a reference, and now resolves: the node it names is nested in the same document.
 *
 * The route renders every claim this entity makes — the titles, the topics, the identifiers, the
 * profiles — from the same configuration the `Person` node is built from. That is the point of the
 * page: structured data a visitor cannot check on the page is an assertion, and a `ProfilePage`
 * whose subject is invisible is the version of that mistake Google reads most closely.
 */
export const authorPage: JsonLd = seo.page({
  path: '/author',
  name: 'Author',
  description:
    'The person who publishes this boilerplate, and every claim the structured-data graph makes about them, visible on the page.',
  type: 'ProfilePage',
  breadcrumb: [
    { name: 'Home', path: '/' },
    { name: 'Author', path: '/author' }
  ],
  extra: { ...pageEntityLinks, mainEntity: entityNode }
})

/** The contact route. */
export const contactPage: JsonLd = seo.page({
  path: '/contact',
  name: 'Contact',
  description: 'Get in touch, plus answers to the questions this boilerplate is most often asked.',
  type: 'ContactPage',
  breadcrumb: [
    { name: 'Home', path: '/' },
    { name: 'Contact', path: '/contact' }
  ],
  extra: pageEntityLinks
})

/**
 * The FAQ rendered on the contact route.
 *
 * @remarks
 * Built from the same `faqItems` array `ContactView.vue` renders, so the markup and the visible copy
 * cannot diverge. Anchored at `/contact#faq` rather than the page's own `@id`, because the page is a
 * `ContactPage` and the FAQ is a distinct node hosted on it.
 */
export const contactFaq: JsonLd = seo.faqPage(faqItems, seo.ids.page('/contact', 'faq'))

/**
 * The documentation route, and the `ItemList` of documents it renders.
 *
 * @remarks
 * One entity rather than two. `seo.itemList()` produces the `CollectionPage` *and* the `ItemList`
 * that is its `mainEntity`, anchored at this path's `#collection` fragment — so unlike the contact
 * route, where the page and its FAQ are genuinely separate nodes, there is nothing here to declare
 * twice. A second `seo.page()` for the same path would be a second collection page describing the
 * same URL.
 *
 * Two properties are overridden through `extra`, which the builder applies last:
 *
 * - **`about`**, for the reason every other page overrides it — the builder resolves it against
 *   this site's own anchor, and this site's entity may be declared elsewhere. Without this the
 *   graph would name an `@id` that no node defines; `entities.spec.ts` walks for exactly that.
 * - **`breadcrumb`**, which `seo.itemList()` does not model. Built from the same helper
 *   `seo.page()` uses internally, so the trail is identical in shape to every other route's.
 *
 * The items point at documents on the source host rather than at routes here — see the remarks in
 * `documentation.ts`. That is why each is a `TechArticle` with an external `url`: the node is
 * declared once, on this origin, and describes a file that lives somewhere else.
 */
export const documentationPage: JsonLd = seo.itemList({
  path: '/documentation',
  name: 'Documentation',
  description:
    'Every document this project publishes: the architecture overview, the guides, and the conventions a contributor is held to.',
  items: documentationConfig.entries,
  map: (entry, id) => ({
    '@type': 'TechArticle',
    '@id': id,
    name: entry.title,
    description: entry.description,
    /*
     * The summary the page renders, as one string. `abstract` is the property for exactly this — a
     * standalone précis of the work — and it is safe to publish only because the same sentences are
     * on the page: an abstract nobody can read on the site is a description of a document rather
     * than a summary of one.
     */
    abstract: entry.summary.join(' '),
    url: entry.url,
    inLanguage: siteConfig.language,
    author: entityRef
  }),
  extra: {
    about: entityRef,
    breadcrumb: seo.breadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Documentation', path: '/documentation' }
    ])
  }
})

/**
 * The catch-all route.
 *
 * @remarks
 * Carries `noindex, follow` from the route table and is excluded from the sitemap, so this entity
 * should never be indexed. It exists so the 404 document is not the one page on the site with no
 * structured data at all.
 */
export const notFoundPage: JsonLd = seo.page({
  path: '/404',
  name: 'Page not found',
  description: 'The page you are looking for does not exist or has moved.',
  extra: pageEntityLinks
})
